import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * WelcomeCarousel — auto-rotating hero carousel for the public WelcomePage.
 *
 * Order rules (per product spec v1.7.4):
 *   1) slogan branding slide        (carousel_06)
 *   2) inventario screenshot        (carousel_01)
 *   3) claim branding slide         (carousel_07)
 *   4–7) shuffled at mount:
 *       - cassa (carousel_02)
 *       - prodotti (carousel_03)
 *       - vista cliente (carousel_04)
 *       - eventi (carousel_05)
 *
 * Rotation: every 4000ms, fade transition, pause on hover, dots clickable.
 * Images are decorative (no informational text in alt) — captions live in the
 * slides themselves.
 */
const FIXED_SLIDES = [
  { src: '/carousel/carousel_06_branding_slogan.png', alt: 'Kyloo — Il tuo negozio, tutto in un\'app' },
  { src: '/carousel/carousel_01_inventario.png',      alt: 'Schermata Inventario di Kyloo' },
  { src: '/carousel/carousel_07_branding_claim.png',  alt: 'Kyloo — Più semplice, più veloce, più tuo' },
];

const SHUFFLE_POOL = [
  { src: '/carousel/carousel_02_cassa.png',         alt: 'Schermata Cassa di Kyloo' },
  { src: '/carousel/carousel_03_prodotti.png',      alt: 'Gestione prodotti su Kyloo' },
  { src: '/carousel/carousel_04_vista_cliente.png', alt: 'Vista cliente del negozio' },
  { src: '/carousel/carousel_05_eventi.png',        alt: 'Gestione eventi su Kyloo' },
];

const ROTATION_MS = 4000;

const shuffle = (arr) => {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export default function WelcomeCarousel({ className = '' }) {
  // Build the final ordered list once per mount (shuffled tail).
  const slides = useMemo(() => [...FIXED_SLIDES, ...shuffle(SHUFFLE_POOL)], []);

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      ROTATION_MS,
    );
    return () => clearInterval(timerRef.current);
  }, [paused, slides.length]);

  // Preload neighbour images to avoid flash on transition.
  useEffect(() => {
    const next = (index + 1) % slides.length;
    const img = new Image();
    img.src = slides[next].src;
  }, [index, slides]);

  const goTo = (i) => {
    setIndex(i);
    // Pause briefly when the user interacts so the new slide gets full time.
    setPaused(true);
    setTimeout(() => setPaused(false), 800);
  };

  return (
    <div
      className={`relative w-full ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="welcome-carousel"
    >
      <div
        className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-blue-950/30"
        aria-roledescription="carousel"
        aria-label="Funzionalità di Kyloo"
      >
        {slides.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            loading={i === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${
              i === index ? 'opacity-100' : 'opacity-0'
            }`}
            data-testid={`welcome-carousel-slide-${i}`}
            aria-hidden={i !== index}
          />
        ))}
      </div>

      {/* Dot indicators */}
      <div
        className="mt-4 flex items-center justify-center gap-2"
        role="tablist"
        aria-label="Naviga tra le funzionalità"
      >
        {slides.map((s, i) => (
          <button
            key={s.src}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Vai alla slide ${i + 1}`}
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === index
                ? 'w-8 bg-white'
                : 'w-2 bg-white/40 hover:bg-white/70'
            }`}
            data-testid={`welcome-carousel-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}
